// MyPathPage.jsx — Phase-9 lean primary/discovery view.
//
// Routes:
//   /my/path       → primary previews (max 5)
//   /my/discovery  → discovery previews (max 2)
//
// CALLS LEAN ENDPOINTS — never fetches the full catalogue. Backend filters
// before sending, so non-admin users only receive their selected previews.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, ChevronRight, ArrowLeft, Sparkles, Compass, Star, Layers,
  Briefcase, GraduationCap, Landmark, Rocket, Sparkles as SparklesIcon,
  Edit3, Lock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { useAuth } from '../lib/auth';
import {
  fetchMyPrimaryPreviews, fetchMyDiscoveryPreviews, fetchPathCatalogue,
} from '../lib/pathsApi';

const ICONS = {
  'graduation-cap': GraduationCap, 'layers': Layers, 'briefcase': Briefcase,
  'sparkles': SparklesIcon, 'landmark': Landmark, 'rocket': Rocket,
};
const COLOR = {
  amber:   'from-amber-500 to-amber-700',
  teal:    'from-teal-500 to-teal-700',
  blue:    'from-blue-500 to-blue-700',
  purple:  'from-purple-500 to-purple-700',
  emerald: 'from-emerald-500 to-emerald-700',
  rose:    'from-rose-500 to-rose-700',
};

// Unified detail URL — same route for every path. The PreviewDetailPage
// renders the normalised shape from `/api/previews/:path_key/:id` so we
// no longer maintain 6 path-specific detail routes.
function detailLink(pathKey, p) {
  const id = encodeURIComponent(String(p.id || p.catalog_number || p.slug || ''));
  return `/preview/${pathKey}/${id}`;
}

export default function MyPathPage({ mode = 'primary' }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [catalogue, setCatalogue] = useState([]);

  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      navigate('/signin', { replace: true });
      return;
    }
    // After 10th / Inter is a QUIZ-ONLY path. Users on this path must never
    // see preview slots / "Pick X of Y" UI — bounce them to the quiz entry.
    if (user.primary_path === 'after_10th_inter') {
      navigate('/career-clarity', { replace: true });
      return;
    }
    // Inner Specializations = program → branch → pick up to 3 (NEW flow,
    // not the old 'pick up to 5 previews' page). Redirect to its own picker.
    if (user.primary_path === 'specializations') {
      navigate('/specializations', { replace: true });
      return;
    }
    if (user.primary_path === 'job_roles') {
      navigate('/job-roles', { replace: true });
      return;
    }
    if (user.primary_path === 'ai_pathway') {
      navigate('/ai-pathway', { replace: true });
      return;
    }
    if (user.primary_path === 'blue_collar') {
      navigate('/blue-collar', { replace: true });
      return;
    }
    if (user.primary_path === 'banking') {
      navigate('/banking', { replace: true });
      return;
    }
    if (user.primary_path === 'ownpath' || user.primary_path === 'entrepreneurship') {
      navigate('/ownpath', { replace: true });
      return;
    }
    (async () => {
      try {
        const [resp, cat] = await Promise.all([
          mode === 'primary' ? fetchMyPrimaryPreviews() : fetchMyDiscoveryPreviews(),
          fetchPathCatalogue(),
        ]);
        setData(resp);
        setCatalogue(cat);
      } finally { setLoading(false); }
    })();
  }, [mode, user, navigate]);

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <Loader2 className="w-10 h-10 animate-spin text-teal-600"/>
      </div>
    );
  }

  const pathKey = mode === 'primary' ? data?.primary_path : data?.discovery_path;
  const previews = data?.previews || [];
  const limit = data?.limit || (mode === 'primary' ? 5 : 2);
  const meta = catalogue.find((p) => p.key === pathKey);
  const Icon = meta ? (ICONS[meta.icon] || Briefcase) : (mode === 'primary' ? Star : Compass);
  const gradient = meta ? COLOR[meta.color] : (mode === 'primary' ? COLOR.teal : COLOR.purple);

  if (!pathKey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
        <Navbar />
        <div className="pt-28 pb-16 px-4">
          <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-3xl p-8 text-center" data-testid={`${mode}-empty`}>
            {mode === 'primary' ? (
              <>
                <Star className="w-10 h-10 text-gray-300 mx-auto mb-3"/>
                <h2 className="text-lg font-bold text-gray-900 mb-2">No primary path yet</h2>
                <p className="text-sm text-gray-600 mb-4">Pick a path from your dashboard to get started.</p>
                <Button asChild className="btn-primary"><Link to="/dashboard">Open dashboard</Link></Button>
              </>
            ) : (
              <>
                <Compass className="w-10 h-10 text-gray-300 mx-auto mb-3"/>
                <h2 className="text-lg font-bold text-gray-900 mb-2">No discovery path yet</h2>
                <p className="text-sm text-gray-600 mb-4">Choose one discovery path and select 2 previews to peek inside.</p>
                <Button asChild className="btn-primary"><Link to="/dashboard">Back to dashboard</Link></Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-5xl mx-auto">

          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <Link to="/dashboard" className="inline-flex items-center text-sm font-semibold text-gray-700 hover:text-teal-700" data-testid="back-to-dashboard">
              <ArrowLeft size={14} className="mr-1.5"/> Dashboard
            </Link>
            <div className="flex gap-2">
              {mode === 'primary' ? (
                <Button asChild variant="outline" size="sm" data-testid="edit-primary-link">
                  <Link to="/my/path/edit"><Edit3 size={12} className="mr-1.5"/> Edit my 5 picks</Link>
                </Button>
              ) : (
                <>
                  <Button asChild variant="outline" size="sm" data-testid="edit-discovery-link">
                    <Link to="/my/discovery/edit"><Edit3 size={12} className="mr-1.5"/> Edit discovery picks</Link>
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Hero */}
          <div className={`bg-gradient-to-br ${gradient} rounded-3xl p-6 md:p-8 text-white shadow-xl mb-8`} data-testid={`${mode}-hero`}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Icon className="w-6 h-6 md:w-7 md:h-7 text-white"/>
              </div>
              <div className="flex-1">
                <div className="text-white/80 text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1">
                  {mode === 'primary' ? <><Sparkles size={11}/> Your primary path</> : <><Compass size={11}/> Your discovery path (browse-only)</>}
                </div>
                <h1 className="text-2xl md:text-3xl font-bold leading-tight">{meta?.label || pathKey}</h1>
                <p className="text-white/90 text-sm mt-1">{meta?.tagline}</p>
                <div className="mt-3 inline-block bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold" data-testid={`${mode}-counter`}>
                  {mode === 'primary' ? 'Previews selected' : 'Discovery previews selected'}: <strong>{previews.length}/{limit}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Section title */}
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-3 px-1">
            {mode === 'primary' ? 'Your selected previews' : 'Your discovery previews'}
          </h2>

          {previews.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center" data-testid={`${mode}-no-picks`}>
              <Lock className="w-8 h-8 text-gray-300 mx-auto mb-2"/>
              <p className="text-sm text-gray-700 mb-4">
                You haven&apos;t selected any {mode} previews yet.
              </p>
              <Button asChild className="btn-primary">
                <Link to={mode === 'primary' ? '/my/path/edit' : '/my/discovery/edit'}>Pick {mode === 'primary' ? `up to ${limit} previews` : `${limit} previews`}</Link>
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${mode}-previews-grid`}>
              {previews.map((p, i) => {
                const title = p.role_name || p.title || p.name || `Preview ${i + 1}`;
                const summary = p.shift_in_one_scene || p.summary
                              || p.oneLiner || p.headline || '';
                const id = String(p.catalog_number || p.id || p.slug);
                return (
                  <Link
                    key={id}
                    to={detailLink(pathKey, p)}
                    className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-lg hover:border-teal-300 transition-all group flex flex-col"
                    data-testid={`preview-card-${id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      {mode === 'primary' && p._kind === 'primary' && (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-teal-50 text-teal-800">★ Primary</span>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all ml-auto"/>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1 leading-tight">{title}</h3>
                    {summary && <p className="text-xs text-gray-600 line-clamp-2 mt-1">{summary}</p>}
                    <span className="text-teal-700 text-xs font-semibold mt-auto pt-3 group-hover:underline">See full preview →</span>
                  </Link>
                );
              })}
              {/* Empty slot placeholders */}
              {Array.from({ length: Math.max(0, limit - previews.length) }).map((_, i) => (
                <Link
                  key={`slot-${i}`}
                  to={mode === 'primary' ? '/my/path/edit' : '/my/discovery/edit'}
                  className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-5 hover:border-teal-300 transition-all flex items-center justify-center text-gray-400 text-xs"
                  data-testid={`${mode}-empty-slot-${i}`}
                >
                  + Pick {previews.length + i + 1}
                </Link>
              ))}
            </div>
          )}

          <p className="text-center text-xs text-gray-400 mt-10">
            {mode === 'primary'
              ? `Your primary path shows only your ${limit} selected previews. Everything else is locked by design.`
              : `Your discovery window shows only your ${limit} selected previews — keeps your focus tight.`}
          </p>

        </div>
      </div>
    </div>
  );
}
